import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { KeyRound, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('Le password non corrispondono');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('La password deve essere di almeno 6 caratteri');
            return;
        }

        setLoading(true);
        try {
            await resetPassword(token, newPassword);
            setSuccess(true);
            toast.success('Password resettata con successo!');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore nel reset');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F2F5F2] p-4">
                <Card className="w-full max-w-md border border-[#CBD5E1] shadow-none">
                    <CardHeader>
                        <CardTitle className="text-[#B71C1C]">Link non valido</CardTitle>
                        <CardDescription>Il link di reset non è valido o è scaduto.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Link to="/forgot-password">
                            <Button className="w-full bg-brand">Richiedi nuovo link</Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F2F5F2] p-4">
                <Card className="w-full max-w-md border border-[#CBD5E1] shadow-none">
                    <CardHeader className="space-y-1 text-center">
                        <CheckCircle className="h-12 w-12 text-[#34C759] mx-auto mb-4" />
                        <CardTitle className="text-2xl text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                            Password aggiornata!
                        </CardTitle>
                        <CardDescription className="text-[#64748B]">
                            La tua password è stata resettata con successo. Ora puoi accedere con la nuova password.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Link to="/login">
                            <Button className="w-full bg-brand">
                                Vai al login
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F2F5F2] p-4">
            <Card className="w-full max-w-md border border-[#CBD5E1] shadow-none">
                <CardHeader className="space-y-1">
                    <div className="flex items-center gap-2 mb-2">
                        <KeyRound className="h-8 w-8 text-brand" />
                    </div>
                    <CardTitle className="text-2xl text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        Nuova password
                    </CardTitle>
                    <CardDescription className="text-[#64748B]">
                        Inserisci la tua nuova password
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="newPassword" className="text-[#0F172A]">Nuova password</Label>
                            <Input
                                id="newPassword"
                                data-testid="reset-password-new-input"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Inserisci nuova password"
                                required
                                className="border-[#CBD5E1] focus:ring-[color:var(--brand-color)]"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-[#0F172A]">Conferma password</Label>
                            <Input
                                id="confirmPassword"
                                data-testid="reset-password-confirm-input"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Conferma nuova password"
                                required
                                className="border-[#CBD5E1] focus:ring-[color:var(--brand-color)]"
                            />
                        </div>
                        <Button 
                            type="submit" 
                            data-testid="reset-password-submit-btn"
                            className="w-full bg-brand text-white"
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Reimposta password
                        </Button>
                    </form>
                    <div className="mt-4 text-center">
                        <Link to="/login" className="text-sm text-brand hover:underline flex items-center justify-center gap-1">
                            <ArrowLeft className="h-4 w-4" />
                            Torna al login
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
